import * as React from "react";
import { useMemo } from "react";
import { PluginStore } from "graylog-web-plugin/plugin";
import styled from "styled-components";
import { getPathnameWithoutId } from "util/URLUtils";
import { Col, Row } from "components/bootstrap";
import { Wizard } from "components/common";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useLocation from "routing/useLocation";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import EventDefinitionFormControls from "components/event-definitions/event-definition-form/EventDefinitionFormControls";
import EventDetailsForm from "./EventDetailsForm";
import EventConditionForm from "./EventConditionForm";
import FieldsForm from "./FieldsForm";
import NotificationsForm from "./NotificationsForm";
import EventDefinitionSummary from "./EventDefinitionSummary";
import ShareForm from "./ShareForm";
const WizardContainer = styled.div`
  margin-bottom: 10px;
`;
const STEP_KEYS = {
  EVENT_DETAILS: "event-details",
  CONDITION: "condition",
  FIELDS: "fields",
  NOTIFICATIONS: "notifications",
  SHARE: "Share",
  SUMMARY: "summary"
};
export const getStepKeys = (isNew, hideFieldsStep = false) => [
  STEP_KEYS.EVENT_DETAILS,
  STEP_KEYS.CONDITION,
  ...hideFieldsStep ? [] : [STEP_KEYS.FIELDS],
  STEP_KEYS.NOTIFICATIONS,
  ...isNew ? [STEP_KEYS.SHARE] : [],
  STEP_KEYS.SUMMARY
];
const STEP_TELEMETRY_KEYS = [
  TELEMETRY_EVENT_TYPE.EVENTDEFINITION_DETAILS.STEP_CLICKED,
  TELEMETRY_EVENT_TYPE.EVENTDEFINITION_CONDITION.STEP_CLICKED,
  TELEMETRY_EVENT_TYPE.EVENTDEFINITION_FIELDS.STEP_CLICKED,
  TELEMETRY_EVENT_TYPE.EVENTDEFINITION_NOTIFICATIONS.STEP_CLICKED,
  TELEMETRY_EVENT_TYPE.EVENTDEFINITION_SUMMARY.STEP_CLICKED
];
const getConditionPlugin = (type) => {
  if (type === void 0) {
    return { displayName: null };
  }
  return PluginStore.exports("eventDefinitionTypes").find((edt) => edt.type === type) || {};
};
const EventDefinitionForm = ({
  action = "edit",
  activeStep,
  canEdit,
  currentUser,
  defaults,
  entityTypes,
  eventDefinition,
  formControls: FormControls = EventDefinitionFormControls,
  notifications,
  onCancel,
  onChange,
  onChangeStep,
  onSubmit,
  validation,
  hideFieldsStep = false
}) => {
  var _a;
  const { pathname } = useLocation();
  const sendTelemetry = useSendTelemetry();
  const handleSubmit = (event) => {
    if (event) {
      event.preventDefault();
    }
    onSubmit();
  };
  const defaultStepProps = {
    key: eventDefinition.id,
    // Recreate components if ID changed
    action,
    entityTypes,
    eventDefinition,
    onChange,
    validation,
    currentUser
  };
  const canEditCondition = useMemo(
    () => canEdit || eventDefinition._scope.toUpperCase() === "ILLUMINATE",
    [canEdit, eventDefinition._scope]
  );
  const eventProcedureId = (eventDefinition == null ? void 0 : eventDefinition.event_procedure) || void 0;
  const eventDefinitionType = getConditionPlugin(eventDefinition.config.type);
  const isNew = action === "create";
  const allSteps = [
    {
      key: STEP_KEYS.EVENT_DETAILS,
      title: "Event Details",
      component: /* @__PURE__ */ React.createElement(EventDetailsForm, { ...defaultStepProps, eventDefinitionEventProcedure: eventProcedureId, canEdit })
    },
    {
      key: STEP_KEYS.CONDITION,
      title: (_a = eventDefinitionType == null ? void 0 : eventDefinitionType.displayName) != null ? _a : "Condition",
      component: /* @__PURE__ */ React.createElement(EventConditionForm, { ...defaultStepProps, canEdit: canEditCondition })
    },
    {
      key: STEP_KEYS.FIELDS,
      title: "Fields",
      component: /* @__PURE__ */ React.createElement(FieldsForm, { ...defaultStepProps, canEdit })
    },
    {
      key: STEP_KEYS.NOTIFICATIONS,
      title: "Notifications",
      component: /* @__PURE__ */ React.createElement(NotificationsForm, { ...defaultStepProps, notifications, defaults })
    },
    {
      key: STEP_KEYS.SHARE,
      title: "Share",
      component: /* @__PURE__ */ React.createElement(ShareForm, { ...defaultStepProps })
    },
    {
      key: STEP_KEYS.SUMMARY,
      title: "Summary",
      component: /* @__PURE__ */ React.createElement(
        EventDefinitionSummary,
        {
          eventDefinition,
          currentUser,
          notifications,
          validation
        }
      )
    }
  ];
  const steps = allSteps.filter((step) => {
    if (step.key === STEP_KEYS.FIELDS && hideFieldsStep) return false;
    if (step.key === STEP_KEYS.SHARE && !isNew) return false;
    return true;
  });
  const currentStepKeys = steps.map((step) => step.key);
  const activeStepIndex = currentStepKeys.indexOf(activeStep);
  const handleStepChange = (nextStep) => {
    var _a2;
    sendTelemetry(STEP_TELEMETRY_KEYS[currentStepKeys.indexOf(nextStep)], {
      app_pathname: getPathnameWithoutId(pathname),
      app_section: action === "create" ? "new-event-definition" : "edit-event-definition",
      app_action_value: "event-definition-step",
      current_step: (_a2 = steps[activeStepIndex]) == null ? void 0 : _a2.title
    });
    onChangeStep(nextStep);
  };
  const openPrevPage = () => {
    var _a2;
    sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_PREVIOUS_CLICKED, {
      app_pathname: getPathnameWithoutId(pathname),
      app_section: action === "create" ? "new-event-definition" : "edit-event-definition",
      app_action_value: "previous-button",
      current_step: (_a2 = steps[activeStepIndex]) == null ? void 0 : _a2.title
    });
    const previousStep = activeStepIndex > 0 ? currentStepKeys[activeStepIndex - 1] : void 0;
    onChangeStep(previousStep);
  };
  const openNextPage = () => {
    var _a2;
    sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_NEXT_CLICKED, {
      app_pathname: getPathnameWithoutId(pathname),
      app_section: action === "create" ? "new-event-definition" : "edit-event-definition",
      app_action_value: "next-button",
      current_step: (_a2 = steps[activeStepIndex]) == null ? void 0 : _a2.title
    });
    const nextStep = currentStepKeys[activeStepIndex + 1];
    onChangeStep(nextStep);
  };
  return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(WizardContainer, null, /* @__PURE__ */ React.createElement(
    Wizard,
    {
      steps,
      activeStep,
      onStepChange: handleStepChange,
      horizontal: true,
      justified: true,
      containerClassName: "",
      hidePreviousNextButtons: true
    }
  )), /* @__PURE__ */ React.createElement(
    FormControls,
    {
      activeStepIndex,
      action,
      onOpenPrevPage: openPrevPage,
      onOpenNextPage: openNextPage,
      steps,
      onSubmit: handleSubmit,
      onCancel
    }
  )));
};
export default EventDefinitionForm;
